import { Component, computed, inject, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faGlobe } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import { ConfirmDialogComponent, UiAdminModule } from '@eiq/ui';
import { LanguageVariableToken } from '@empathy/survey/base';
import { Translation } from '@empathy/survey-core';

import { RemoveConditionalsRequest } from '../../../models/conditions/remove-conditionals-request.model';
import { EditorTranslationsService } from '../../../services/editor-translations.service';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { LanguageListItemComponent } from '../language-list-item/language-list-item.component';
import { LanguagesEmptyComponent } from '../languages-empty/languages-empty.component';

@Component({
  standalone: true,
  selector: 'empathy-language-settings',
  templateUrl: 'language-settings.component.html',
  imports: [FontAwesomeModule, LanguagesEmptyComponent, LanguageListItemComponent, UiAdminModule]
})
export class LanguageSettingsComponent {
  addLanguage = output();

  translations$ = computed(() => this.translationsService.localizedTranslations$());
  languageMap$ = computed(() => Object.fromEntries(this.translationsService.languages$().map((l) => [l.code, l])));

  loaded$ = computed(() => this.translationsService.translationsLoaded$() && this.translationsService.languagesLoaded$());
  canAddLanguage$ = computed(() => !!this.translationsService.defaultTranslation$());

  faGlobe = faGlobe;
  faPlus = faPlus;

  private translationsService = inject(EditorTranslationsService);
  private surveyEditorService = inject(SurveyEditorService);
  private surveyConditionalService = inject(SurveyConditionalsService);
  private dialog = inject(MatDialog);

  onRemoveLanguage(translation: Translation): void {
    const languageVariableCtrl = this.surveyEditorService
      .surveyEditor$()
      .sources.systemSourceControl?.controls.variables.controls.find((variableCtrl) => variableCtrl.controls.variableToken.value === LanguageVariableToken);

    const activeQId = languageVariableCtrl.value.id;
    const optionId = languageVariableCtrl.value.options.find((option) => option.value === translation.code).id;

    const affectedQuestionConditions = this.surveyEditorService.surveyEditor$().affectedQuestionsOnConditionByOptionId(activeQId, optionId);
    const affectedHiddenQuestionConditions = this.surveyEditorService.surveyEditor$().affectedHiddenQuestionsOnConditionByHiddenOptionId(activeQId, optionId);

    const affectedConditions = affectedQuestionConditions.concat(affectedHiddenQuestionConditions);

    let confirmText = 'Are you sure you want to delete this language? This action cannot be undone.';

    if (affectedConditions.length > 0) {
      const conditions = affectedConditions.join(', ');
      confirmText += `</br></br>This language is used as a conditional setting on <strong>${conditions}</strong>. If you continue, the condition/s will be removed.</br></br>`;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Delete Language',
          confirmText,
          confirmBtn: 'Delete',
          cancelBtn: 'Cancel',
          color: 'warn'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.translationsService.removeLanguage(translation.code).subscribe(() => {
            this.surveyEditorService.surveyEditor$().sources.removeLanguageVariableOption(translation.code);
            this.surveyConditionalService.removeHiddenConditions(new RemoveConditionalsRequest(activeQId, affectedHiddenQuestionConditions, optionId));
            affectedQuestionConditions.forEach((qId) =>
              this.surveyConditionalService.removeConditions(new RemoveConditionalsRequest(qId, [activeQId], optionId))
            );
          });
        }
      });
  }
}
